import React from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from 'react-bootstrap';
import {
    PHOTO_ACCEPT,
    PHOTO_LIMIT,
    PHOTO_MAX_BYTES,
} from '@/shared/constants/listings';
import '../listings.css';

const MAX_MB = Math.round(PHOTO_MAX_BYTES / (1024 * 1024));

// The rejection message is the parent's state, not this component's: the form
// has to refuse a submit while a selection is invalid, and it can only do that
// if it knows. Local state here would leave the member looking at a blocking
// error next to a submit button that posts anyway.
function PhotoUploader({
    existing = [],
    files = [],
    error = '',
    onFilesChange,
    onError,
    onRemoveExisting,
}) {
    const reject = (event, message) => {
        onError(message);
        onFilesChange([]);
        // Clearing the DOM value too, or the browser keeps reporting the
        // rejected files as the current selection.
        event.target.value = '';
    };

    const handleChange = (event) => {
        const chosen = Array.from(event.target.files || []);
        const tooBig = chosen.find((file) => file.size > PHOTO_MAX_BYTES);
        if (tooBig) {
            reject(event, `${tooBig.name} is larger than ${MAX_MB} MB.`);
            return;
        }
        if (existing.length + chosen.length > PHOTO_LIMIT) {
            reject(event, `A listing holds at most ${PHOTO_LIMIT} photos.`);
            return;
        }
        onError('');
        onFilesChange(chosen);
    };

    const describedBy = error
        ? 'listing-photos-feedback listing-photos-help'
        : 'listing-photos-help';

    return (
        <Form.Group controlId="listing-photos" className="mb-3">
            <Form.Label>Photos</Form.Label>
            <Form.Control
                type="file"
                multiple
                accept={PHOTO_ACCEPT}
                onChange={handleChange}
                isInvalid={Boolean(error)}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={describedBy}
            />
            <Form.Control.Feedback id="listing-photos-feedback" type="invalid">
                {error}
            </Form.Control.Feedback>
            <Form.Text id="listing-photos-help">
                Up to {PHOTO_LIMIT} photos, {MAX_MB} MB each. JPEG, PNG, or
                WebP.
            </Form.Text>
            {files.length > 0 && (
                <ul className="mt-2 mb-0">
                    {files.map((file) => (
                        <li key={file.name}>{file.name}</li>
                    ))}
                </ul>
            )}
            {existing.length > 0 && (
                <div className="d-flex flex-wrap gap-3 mt-3">
                    {existing.map((photo, index) => (
                        <div key={photo._id}>
                            <img
                                src={photo.url}
                                alt={`Photo ${index + 1}`}
                                className="afe-photo-thumb d-block mb-1"
                            />
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => onRemoveExisting(photo._id)}
                            >
                                Remove photo {index + 1}
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </Form.Group>
    );
}

PhotoUploader.propTypes = {
    existing: PropTypes.arrayOf(
        PropTypes.shape({ _id: PropTypes.string, url: PropTypes.string })
    ),
    files: PropTypes.array,
    error: PropTypes.string,
    onFilesChange: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onRemoveExisting: PropTypes.func,
};

export default PhotoUploader;
